"use client";
import { BackButton } from "@/components/common/BackButton";

import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import {
  Mail,
  LockKeyhole,
  Check,
  ArrowRight,
  CircleAlert,
} from "lucide-react";
import { CommonInput } from "@/components/common/CommonInput";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import * as yup from "yup";
import { authRepository } from "@/repository/authRepository";
import { localAuthRepository } from "@/repository/localAuthRepository";
import { newRegistrationRepository } from "@/repository/newRegistrationRepository";
import { InlineLoader } from "@/components/ui/loader";
import { PasswordResetDialog } from "./PasswordResetDialog";
import Wave from "react-wavify";
import { isPlatformRole } from "@/lib/platformRoles";
import { getStaffLoginErrorMessage } from "./staff-login-error";

// --- Validation Schemas ---

const loginSchema = yup.object().shape({
  email: yup
    .string()
    .required("メールアドレスを入力してください")
    .email("正しいメールアドレスを入力してください"),
  password: yup.string().required("パスワードを入力してください"),
});

const signupSchema = yup.object().shape({
  email: yup
    .string()
    .required("メールアドレスを入力してください")
    .email("正しいメールアドレスを入力してください"),
  password: yup
    .string()
    .required("パスワードを入力してください")
    .min(8, "パスワードは8文字以上で入力してください"),
  confirmPassword: yup
    .string()
    .required("パスワード（確認）を入力してください")
    .oneOf([yup.ref("password")], "パスワードが一致しません"),
});

type LoginForm = yup.InferType<typeof loginSchema>;
type SignupForm = yup.InferType<typeof signupSchema>;

const extractApiMessage = (error: unknown, fallback: string): string => {
  const message = (
    error as { response?: { data?: { message?: string | string[] } } }
  )?.response?.data?.message;
  if (Array.isArray(message)) {
    return message[0] ?? fallback;
  }
  return typeof message === "string" && message.length > 0 ? message : fallback;
};

const getStatus = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } })?.response?.status;

export function WelcomeScreen({ sessionNotice }: { sessionNotice?: string }) {
  const { toast } = useToast();
  const [mode, setMode] = useState<"login" | "signup">("login");
  const [isPasswordResetOpen, setIsPasswordResetOpen] = useState(false);

  // Login State
  const [loginForm, setLoginForm] = useState<LoginForm>({
    email: "",
    password: "",
  });
  const [loginErrors, setLoginErrors] = useState<
    Partial<Record<keyof LoginForm, string>>
  >({});
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [loginStep, setLoginStep] = useState<"credentials" | "otp">(
    "credentials"
  );
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [otpCode, setOtpCode] = useState("");
  const [rememberDevice, setRememberDevice] = useState(false);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [isResendingOtp, setIsResendingOtp] = useState(false);

  // Signup State
  const [signupForm, setSignupForm] = useState<SignupForm>({
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [signupErrors, setSignupErrors] = useState<
    Partial<Record<keyof SignupForm, string>>
  >({});
  const [isSignupLoading, setIsSignupLoading] = useState(false);
  const [isSignupSubmitted, setIsSignupSubmitted] = useState(false);

  // --- Handlers ---

  const handleLoginChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLoginForm((prev) => ({ ...prev, [e.target.id]: e.target.value }));
  };

  const handleSignupChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSignupForm((prev) => ({ ...prev, [e.target.id]: e.target.value }));
  };

  const completeLogin = async (accessToken: string) => {
    // リフレッシュトークンはサーバーが httpOnly Cookie にセットするため、
    // ブラウザではアクセストークンだけを保持する。
    localAuthRepository.setToken(accessToken);
    const me = await authRepository.fetchIamForce();

    sessionStorage.setItem("showLoginSuccessToast", "true");
    location.assign(
      isPlatformRole(me.data?.role) ? "/operator/tenant-payments" : "/dashboard"
    );
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoginLoading(true);
    setLoginError(null);

    try {
      const validatedData = await loginSchema.validate(loginForm, {
        abortEarly: false,
      });
      setLoginErrors({});
      const res = await authRepository.login({
        email: validatedData.email,
        password: validatedData.password,
      });

      if (res.data.twoFactorRequired && res.data.challengeId) {
        setChallengeId(res.data.challengeId);
        setOtpCode("");
        setRememberDevice(false);
        setOtpError(null);
        setLoginStep("otp");
        return;
      }

      if (!res.data.accessToken) {
        setLoginError(
          "ログイン結果を確認できませんでした。再度お試しください。"
        );
        return;
      }

      await completeLogin(res.data.accessToken);
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        const formattedErrors: Partial<Record<keyof LoginForm, string>> = {};
        error.inner.forEach((err) => {
          if (err.path)
            formattedErrors[err.path as keyof LoginForm] = err.message;
        });
        setLoginErrors(formattedErrors);
        return;
      }
      console.error("Login error:", error);
      const loginErrorMessage = getStaffLoginErrorMessage(error);
      setLoginError(loginErrorMessage);
      toast({
        variant: "destructive",
        title: "ログインに失敗しました",
        description: loginErrorMessage,
      });
    } finally {
      setIsLoginLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!challengeId) return;

    setIsLoginLoading(true);
    setOtpError(null);
    try {
      const res = await authRepository.verifyTwoFactor({
        challengeId,
        code: otpCode,
        rememberDevice,
      });

      if (res.data.trustedDeviceToken) {
        localAuthRepository.setTrustedDeviceToken(res.data.trustedDeviceToken);
      }
      await completeLogin(res.data.accessToken);
    } catch (error) {
      console.error("2FA verify error:", error);
      const fallback =
        getStatus(error) === 429
          ? "リクエストが多すぎます。しばらく時間をおいてから再度お試しください。"
          : "認証コードが正しくありません。";
      setOtpError(extractApiMessage(error, fallback));
    } finally {
      setIsLoginLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!challengeId || isResendingOtp) return;

    setIsResendingOtp(true);
    setOtpError(null);
    try {
      await authRepository.resendTwoFactor({ challengeId });
      toast({
        title: "認証コードを再送しました",
        description: "メールに届いた新しいコードをご確認ください。",
      });
    } catch (error) {
      console.error("2FA resend error:", error);
      const fallback =
        getStatus(error) === 429
          ? "リクエストが多すぎます。しばらく時間をおいてから再度お試しください。"
          : "認証コードの再送に失敗しました。時間をおいて再度お試しください。";
      setOtpError(extractApiMessage(error, fallback));
    } finally {
      setIsResendingOtp(false);
    }
  };

  const handleBackToCredentials = () => {
    setLoginStep("credentials");
    setChallengeId(null);
    setOtpCode("");
    setRememberDevice(false);
    setOtpError(null);
  };

  const handleSignupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSignupLoading(true);

    try {
      const validatedData = await signupSchema.validate(signupForm, {
        abortEarly: false,
      });
      setSignupErrors({});
      await newRegistrationRepository.signup({
        email: validatedData.email,
        password: validatedData.password,
        confirmPassword: validatedData.confirmPassword,
      });
      setIsSignupSubmitted(true);
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        const formattedErrors: Partial<Record<keyof SignupForm, string>> = {};
        error.inner.forEach((err) => {
          if (err.path)
            formattedErrors[err.path as keyof SignupForm] = err.message;
        });
        setSignupErrors(formattedErrors);
        toast({
          variant: "destructive",
          title: "入力内容を確認してください",
        });
      } else if (axios.isAxiosError(error)) {
        const data = error.response?.data;
        const message = Array.isArray(data?.message)
          ? data.message.join("\n")
          : (data?.message ?? "アカウント作成に失敗しました");
        toast({
          variant: "destructive",
          title: "アカウント作成に失敗しました",
          description: message,
        });
      } else {
        toast({
          variant: "destructive",
          title: "アカウント作成に失敗しました",
          description: "予期せぬエラーが発生しました。もう一度お試しください。",
        });
      }
    } finally {
      setIsSignupLoading(false);
    }
  };

  const handleRetrySignup = () => {
    setIsSignupSubmitted(false);
    setSignupForm({ email: "", password: "", confirmPassword: "" });
  };

  return (
    <div className="bg-card relative flex min-h-screen w-full items-center justify-center overflow-hidden">
      {/* --- Background Decorations (Waves) --- */}
      <div className="pointer-events-none absolute inset-0 z-0">
        {/* Bottom Gradient Waves */}
        <div className="absolute bottom-0 left-0 w-full opacity-50">
          <Wave
            fill="var(--color-primary)"
            paused={false}
            options={{
              height: 20,
              amplitude: 40,
              speed: 0.15,
              points: 4,
            }}
            style={{ display: "block" }}
            className="h-32 sm:h-48"
          />
        </div>
        <div className="absolute bottom-0 left-0 w-full">
          <Wave
            fill="var(--color-primary)"
            paused={false}
            options={{
              height: 30,
              amplitude: 50,
              speed: 0.1,
              points: 3,
            }}
            style={{ display: "block" }}
            className="h-24 sm:h-40"
          />
        </div>

        {/* Top Right Decoration */}
        <div className="bg-primary/5 absolute -right-20 -top-20 h-96 w-96 rounded-full blur-3xl"></div>
        <div className="absolute left-[-100px] top-40 h-72 w-72 rounded-full bg-[#4dd0e1]/10 blur-3xl"></div>
      </div>

      {/* --- Main Card Content --- */}
      <div className="animate-in fade-in zoom-in-95 relative z-10 w-full max-w-[480px] px-4 duration-500">
        <div className="bg-card rounded-3xl p-8 shadow-[0_20px_50px_rgba(0,0,0,0.1)] md:p-10">
          {/* Logo & Header */}
          <div className="mb-8 flex flex-col items-center text-center">
            <div className="mb-4">
              <img
                src="/logo.png"
                alt="Smart Communication ロゴ"
                width={88}
                height={88}
                className="h-auto w-20 sm:w-[88px]"
              />
            </div>
            <h1 className="text-foreground text-2xl font-bold tracking-tight">
              {mode === "login"
                ? loginStep === "otp"
                  ? "二段階認証"
                  : "おかえりなさい"
                : "アカウント作成"}
            </h1>
            <p className="text-muted-foreground  text-sm">
              {mode === "login"
                ? loginStep === "otp"
                  ? "メールに届いた6桁の認証コードを入力してください"
                  : "アカウントにログインして続けましょう"
                : "必要な情報を入力して登録してください"}
            </p>
          </div>

          {sessionNotice && mode === "login" && (
            <p
              role="alert"
              className="bg-primary/5 text-foreground mb-5 rounded-md px-3 py-2 text-sm leading-6"
            >
              {sessionNotice}
            </p>
          )}

          {/* --- Login Form --- */}
          {mode === "login" && loginStep === "credentials" && (
            <form onSubmit={handleLoginSubmit} className="space-y-5">
              <CommonInput
                id="email"
                label="メールアドレス"
                placeholder="例: yamada@example.com"
                required
                value={loginForm.email}
                onChange={handleLoginChange}
                error={loginErrors.email}
                type="email"
                autoComplete="email"
                icon={Mail}
              />
              <div className="relative">
                <CommonInput
                  id="password"
                  label="パスワード"
                  placeholder="パスワードを入力"
                  required
                  value={loginForm.password}
                  onChange={handleLoginChange}
                  error={loginErrors.password}
                  type="password"
                  autoComplete="current-password"
                  icon={LockKeyhole}
                  togglePassword
                />
                <button
                  type="button"
                  onClick={() => setIsPasswordResetOpen(true)}
                  className="absolute right-0 top-0 text-xs font-medium text-[#f17233] transition-colors hover:text-[#f17233]/80"
                >
                  パスワードをお忘れですか？
                </button>
              </div>

              {loginError && (
                <div className="flex items-start gap-2 rounded bg-[#dc2626]/10 px-3 py-2 text-sm text-[#dc2626]">
                  <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{loginError}</span>
                </div>
              )}

              <Button
                type="submit"
                className="h-11 w-full rounded-xl text-base font-medium"
                disabled={isLoginLoading}
              >
                {isLoginLoading ? (
                  <InlineLoader text="ログイン中..." />
                ) : (
                  "ログイン"
                )}
              </Button>
            </form>
          )}

          {mode === "login" && loginStep === "otp" && (
            <form onSubmit={handleVerifyOtp} className="space-y-5">
              <CommonInput
                id="otpCode"
                label="認証コード"
                placeholder="例: 123456"
                required
                value={otpCode}
                onChange={(e) =>
                  setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                type="text"
                autoComplete="one-time-code"
                icon={LockKeyhole}
                helperText="6桁の数字を入力してください"
              />

              <div className="flex items-center gap-2">
                <Checkbox
                  id="rememberDevice"
                  checked={rememberDevice}
                  onCheckedChange={(checked) =>
                    setRememberDevice(checked === true)
                  }
                />
                <Label
                  htmlFor="rememberDevice"
                  className="cursor-pointer text-sm font-normal"
                >
                  このデバイスを記憶する（次回から認証コードを省略）
                </Label>
              </div>

              {otpError && (
                <div className="flex items-start gap-2 rounded bg-[#dc2626]/10 px-3 py-2 text-sm text-[#dc2626]">
                  <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{otpError}</span>
                </div>
              )}

              <Button
                type="submit"
                className="h-11 w-full rounded-xl text-base font-medium"
                disabled={isLoginLoading || otpCode.length !== 6}
              >
                {isLoginLoading ? (
                  <InlineLoader text="認証中..." />
                ) : (
                  "認証する"
                )}
              </Button>

              <div className="flex items-center justify-between text-sm">
                <BackButton
                  onClick={handleBackToCredentials}
                  disabled={isLoginLoading}
                >
                  戻る
                </BackButton>
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={isResendingOtp || isLoginLoading}
                  className="text-primary hover:text-primary/80 font-medium hover:underline disabled:opacity-50"
                >
                  {isResendingOtp ? "再送中..." : "コードを再送"}
                </button>
              </div>
            </form>
          )}

          {/* --- Signup Form --- */}
          {mode === "signup" && (
            <>
              {isSignupSubmitted ? (
                <div className="animate-in fade-in text-center">
                  <div className="bg-primary/10 mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full">
                    <Check className="text-primary h-8 w-8" />
                  </div>
                  <h3 className="text-foreground  text-xl font-bold">
                    登録完了
                  </h3>
                  <p className="text-muted-foreground mb-6 text-sm">
                    確認メールを送信しました。
                    <br />
                    メール内のリンクから登録を完了してください。
                  </p>
                  <Button
                    onClick={handleRetrySignup}
                    variant="outline"
                    className="border-primary text-primary hover:bg-primary/5 w-full"
                  >
                    再送信する <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSignupSubmit} className="space-y-5">
                  <CommonInput
                    id="email"
                    label="メールアドレス"
                    placeholder="例: yamada@example.com"
                    required
                    value={signupForm.email}
                    onChange={handleSignupChange}
                    error={signupErrors.email}
                    type="email"
                    autoComplete="email"
                    icon={Mail}
                  />
                  <CommonInput
                    id="password"
                    label="パスワード"
                    placeholder="8文字以上"
                    required
                    value={signupForm.password}
                    onChange={handleSignupChange}
                    error={signupErrors.password}
                    type="password"
                    autoComplete="new-password"
                    icon={LockKeyhole}
                    togglePassword
                  />
                  <CommonInput
                    id="confirmPassword"
                    label="パスワード（確認）"
                    placeholder="上記と同じパスワード"
                    required
                    value={signupForm.confirmPassword}
                    onChange={handleSignupChange}
                    error={signupErrors.confirmPassword}
                    type="password"
                    autoComplete="new-password"
                    icon={LockKeyhole}
                    togglePassword
                  />

                  <Button
                    type="submit"
                    className="h-11 w-full rounded-xl text-base font-medium"
                    disabled={isSignupLoading}
                  >
                    {isSignupLoading ? (
                      <InlineLoader text="登録中..." />
                    ) : (
                      "アカウント作成"
                    )}
                  </Button>
                </form>
              )}
            </>
          )}

          {/* --- Switch Mode --- */}
          <div
            className={`mt-8 text-center ${loginStep === "otp" ? "hidden" : ""}`}
          >
            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-gray-100" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card text-muted-foreground px-2">or</span>
              </div>
            </div>

            <div className="text-muted-foreground text-sm">
              {mode === "login" ? (
                <>
                  アカウントをお持ちでないですか？{" "}
                  <button
                    onClick={() => setMode("signup")}
                    className="text-primary hover:text-primary/80 font-semibold transition-colors hover:underline"
                  >
                    新規登録
                  </button>
                </>
              ) : (
                <>
                  すでにアカウントをお持ちですか？{" "}
                  <button
                    onClick={() => setMode("login")}
                    className="text-primary hover:text-primary/80 font-semibold transition-colors hover:underline"
                  >
                    ログイン
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="text-muted-foreground mt-8 text-center text-xs">
          &copy; {new Date().getFullYear()} Smart Communication. All rights
          reserved.
        </div>
      </div>

      {/* Password Reset Dialog */}
      <PasswordResetDialog
        isOpen={isPasswordResetOpen}
        onClose={() => setIsPasswordResetOpen(false)}
        initialEmail={loginForm.email}
      />
    </div>
  );
}
